import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import throttle from 'lodash.throttle';
import { DateSectionType, DateTimeFormat, SECTION_MAX_LENGTH, SECTION_RANGES, getSectionOrder } from './dateTimeConstants';
function getDaysInMonth(year, month) {
    return new Date(year, month, 0).getDate();
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
function createValidDate(day, month, year, hours, minutes) {
    if (day === null || month === null || year === null || hours === null || minutes === null) {
        return null;
    }
    if (day === 0 || month === 0) {
        return null;
    }
    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
    return date;
}
export function useDateTimeField({ value, onChange, format = DateTimeFormat.DateTime }) {
    const sectionOrder = useMemo(() => getSectionOrder(format), [format]);
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
    const [lastModifiedSectionIndex, setLastModifiedSectionIndex] = useState(null);
    const [day, setDay] = useState(null);
    const [month, setMonth] = useState(null);
    const [year, setYear] = useState(null);
    const [hours, setHours] = useState(null);
    const [minutes, setMinutes] = useState(null);
    const isFocusedRef = useRef(false);
    const valueRef = useRef(value);
    valueRef.current = value;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const syncFromValue = (date) => {
        if (!isNullOrUndefined(date)) {
            setDay(date.getDate());
            setMonth(date.getMonth() + 1);
            setYear(date.getFullYear());
            setHours(date.getHours());
            setMinutes(date.getMinutes());
        }
        else {
            setDay(null);
            setMonth(null);
            setYear(null);
            setHours(null);
            setMinutes(null);
        }
    };
    useEffect(() => {
        if (!isFocusedRef.current) {
            const validDate = createValidDate(day, month, year, hours, minutes);
            if (validDate !== null || valueRef.current !== null) {
                syncFromValue(value);
            }
        }
    }, [value]);
    const throttledOnChange = useMemo(() => throttle((d, mo, y, h, mi) => {
        const validDate = createValidDate(d, mo, y, h, mi);
        onChangeRef.current(validDate);
    }, 300), []);
    useEffect(() => {
        throttledOnChange(day, month, year, hours, minutes);
    }, [day, month, year, hours, minutes, throttledOnChange]);
    useEffect(() => {
        return () => {
            throttledOnChange.cancel();
        };
    }, [throttledOnChange]);
    const getSectionValue = useCallback((type) => {
        switch (type) {
            case DateSectionType.DD:
                return day;
            case DateSectionType.MM:
                return month;
            case DateSectionType.YYYY:
                return year;
            case DateSectionType.HH:
                return hours;
            case DateSectionType.mm:
                return minutes;
        }
    }, [day, month, year, hours, minutes]);
    const moveToSection = (nextIndex) => {
        const normalizedIndex = clamp(nextIndex, 0, sectionOrder.length - 1);
        setSelectedSectionIndex(normalizedIndex);
    };
    const moveToNextSection = () => {
        moveToSection(selectedSectionIndex + 1);
    };
    const moveToPreviousSection = () => {
        moveToSection(selectedSectionIndex - 1);
    };
    const updateSectionValue = (type, newValue) => {
        const skipClamp = (type === DateSectionType.DD || type === DateSectionType.MM) && newValue === 0;
        const clampedValue = skipClamp ? 0 : clamp(newValue, SECTION_RANGES[type].min, SECTION_RANGES[type].max);
        switch (type) {
            case DateSectionType.DD:
                setDay(clampedValue);
                break;
            case DateSectionType.MM:
                setMonth(clampedValue);
                break;
            case DateSectionType.YYYY:
                setYear(clampedValue);
                break;
            case DateSectionType.HH:
                setHours(clampedValue);
                break;
            case DateSectionType.mm:
                setMinutes(clampedValue);
                break;
        }
        setLastModifiedSectionIndex(selectedSectionIndex);
    };
    const handleSectionSelect = (index) => {
        moveToSection(index);
    };
    const handleIncrement = (type) => {
        const currentValue = getSectionValue(type);
        const range = SECTION_RANGES[type];
        let newValue = currentValue + 1;
        if (newValue > range.max) {
            newValue = range.min;
        }
        if (type === DateSectionType.DD && month > 0 && year > 0) {
            const maxDay = getDaysInMonth(year, month);
            if (newValue > maxDay) {
                newValue = range.min;
            }
        }
        updateSectionValue(type, newValue);
    };
    const handleDecrement = (type) => {
        const currentValue = getSectionValue(type);
        const range = SECTION_RANGES[type];
        let newValue = currentValue - 1;
        if (newValue < range.min) {
            if (type === DateSectionType.DD && month > 0 && year > 0) {
                const maxDay = getDaysInMonth(year, month);
                newValue = maxDay;
            }
            else {
                newValue = range.max;
            }
        }
        updateSectionValue(type, newValue);
    };
    const handleDelete = (type) => {
        switch (type) {
            case DateSectionType.DD:
                setDay(null);
                break;
            case DateSectionType.MM:
                setMonth(null);
                break;
            case DateSectionType.YYYY:
                setYear(null);
                break;
            case DateSectionType.HH:
                setHours(null);
                break;
            case DateSectionType.mm:
                setMinutes(null);
                break;
        }
        setLastModifiedSectionIndex(selectedSectionIndex);
    };
    const handleDigitInput = (type, digit) => {
        var _a;
        const maxLength = SECTION_MAX_LENGTH[type];
        const currentIndex = sectionOrder.findIndex((section) => section.type === type);
        const shouldClearValue = lastModifiedSectionIndex !== currentIndex;
        const currentValue = getSectionValue(type);
        const currentString = shouldClearValue ? '' : ((_a = currentValue === null || currentValue === void 0 ? void 0 : currentValue.toString()) !== null && _a !== void 0 ? _a : '');
        const newString = currentString.length === maxLength ? digit : currentString + digit;
        const newValue = Number(newString);
        updateSectionValue(type, newValue);
        if (newString.length >= maxLength) {
            if (currentIndex === sectionOrder.length - 1) {
                setSelectedSectionIndex(0);
            }
            else {
                setSelectedSectionIndex(currentIndex + 1);
            }
        }
    };
    const handlePaste = (type, digits) => {
        const currentIndex = sectionOrder.findIndex((section) => section.type === type);
        const newValue = Number(digits);
        updateSectionValue(type, newValue);
        if (currentIndex === sectionOrder.length - 1) {
            setSelectedSectionIndex(0);
        }
        else {
            setSelectedSectionIndex(currentIndex + 1);
        }
    };
    const handleFocus = () => {
        isFocusedRef.current = true;
    };
    const handleBlur = () => {
        isFocusedRef.current = false;
    };
    return {
        sectionOrder,
        selectedSectionIndex,
        getSectionValue,
        handleSectionSelect,
        handleDigitInput,
        handlePaste,
        handleIncrement,
        handleDecrement,
        handleDelete,
        moveToNextSection,
        moveToPreviousSection,
        handleFocus,
        handleBlur
    };
}
